/**
 * `sections` — modello delle tre macro-sezioni della navigazione.
 *
 * L'applicazione è organizzata in tre macro-sezioni, selezionabili dal
 * `SectionSwitcher` nella `Navbar`:
 *  - **Assessment** (`/assessment/*`): analisi personale di skill, competenze,
 *    professioni e certificazioni; accessibile a ogni utente autenticato.
 *  - **Review** (`/review`): pannello per reviewer e manager (e admin).
 *  - **Insights** (`/insights/*`, ex "Livelli"): viste aggregate e di team per
 *    l'analisi HR; accessibile all'admin o agli utenti con `levelsEnabled`.
 *
 * Questo modulo è la fonte unica di verità sia per il selettore di sezione
 * (quali sezioni mostrare, in base ai permessi) sia per i link della sezione
 * attiva (le voci di navigazione della `Navbar`). La sezione attiva si deriva
 * dal primo segmento del path (vedi {@link sectionForPath}).
 *
 * I permessi replicati qui sono DIFENSIVI lato client: l'autorizzazione
 * autorevole resta l'API. La logica di visibilità è identica a quella delle
 * guardie di rotta (`RoleRoute`, `LevelsRoute`).
 */
import type { Role } from '../shared/dto';
import type { IconName } from './ui/Icon';

/** Identificatori stabili delle macro-sezioni. */
export type SectionId =
  | 'assessment'
  | 'review'
  | 'insights'
  | 'externalCv'
  | 'explore';

/** Voce di navigazione all'interno di una sezione. */
export interface SectionNavItem {
  /** Path assoluto di destinazione. */
  to: string;
  /** Etichetta mostrata all'utente (UI copy, in italiano). */
  label: string;
  /** `end` per l'evidenziazione esatta (usato dalla voce radice di sezione). */
  end?: boolean;
}

/** Contesto usato per decidere la visibilità delle sezioni per l'utente. */
export interface SectionAccess {
  /** Ruolo applicativo corrente (`null` se non ancora risolto). */
  role: Role | null;
  /** Flag per-utente che sblocca la sezione Insights (oltre all'admin). */
  levelsEnabled: boolean;
  /** Flag per-utente che sblocca la sezione CV/Candidati (oltre all'admin). */
  cvEnabled: boolean;
}

/** Definizione di una macro-sezione: metadati, guardia di accesso e voci. */
export interface SectionDefinition {
  id: SectionId;
  /** Etichetta della sezione nel selettore (UI copy). */
  label: string;
  /** Descrizione breve mostrata nella card della home (UI copy). */
  description: string;
  /** Icona rappresentativa (dal set `Icon`), usata nella card della home. */
  icon: IconName;
  /** Prefisso di path che identifica la sezione (primo segmento). */
  basePath: string;
  /** Destinazione al passaggio a questa sezione (prima voce). */
  home: string;
  /** Voci di navigazione mostrate nella navbar quando la sezione è attiva. */
  items: SectionNavItem[];
  /** Predica di visibilità della sezione per l'utente corrente (difensiva). */
  canAccess: (access: SectionAccess) => boolean;
  /**
   * Se `true`, la sezione è in fase beta: la card della home mostra un badge
   * "Beta" per segnalare che la funzionalità è ancora in evoluzione.
   */
  beta?: boolean;
}

/**
 * Definizione delle tre sezioni, nell'ordine di presentazione nel selettore.
 * L'ordine riflette il livello di scope crescente: personale → team → azienda.
 */
export const SECTIONS: readonly SectionDefinition[] = [
  {
    id: 'assessment',
    label: 'Assessment',
    description:
      'Analizza le tue skill e competenze, valuta il tuo posizionamento sulle professioni e consulta le tue certificazioni.',
    icon: 'sparkle',
    basePath: '/assessment',
    home: '/assessment',
    items: [
      // Voce radice della sezione: ora è la pagina "Il mio CV" (ex Dashboard),
      // che ne è un superset (vedi la rotta `/assessment` in App.tsx).
      { to: '/assessment', label: 'Il mio CV', end: true },
      { to: '/assessment/skills', label: 'Skill' },
      { to: '/assessment/wizard', label: 'Competenze' },
      { to: '/assessment/jobs', label: 'Professioni' },
      { to: '/assessment/certifications', label: 'Certificazioni' },
    ],
    // Ogni utente autenticato ha accesso alla propria analisi personale.
    canAccess: () => true,
  },
  {
    id: 'review',
    label: 'Review',
    description:
      'Valuta le competenze delle persone del tuo ambito e registra i tuoi giudizi come reviewer o manager.',
    icon: 'check',
    basePath: '/review',
    home: '/review',
    // La sezione Review ha un'unica pagina: esponiamo comunque una sola voce
    // "Team" come sotto-menu, così il gruppo in navbar resta coerente con le
    // altre sezioni (selettore + sotto pagine nella stessa pill).
    items: [{ to: '/review', label: 'Team', end: true }],
    canAccess: ({ role }) =>
      role === 'reviewer' || role === 'manager' || role === 'admin',
  },
  {
    id: 'insights',
    label: 'Insights',
    description:
      'Esplora le viste aggregate di copertura per skill, competenze e certificazioni e analizza il team a supporto delle decisioni HR.',
    icon: 'bar-chart',
    basePath: '/insights',
    home: '/insights',
    // Le voci replicano i tab della pagina Insights (ex "Livelli"), pilotati
    // dalla query string `?view=`. La voce "Team" è la vista di default (`?view`
    // assente); le altre impostano il tab corrispondente.
    items: [
      { to: '/insights', label: 'Team', end: true },
      { to: '/insights?view=competences', label: 'Competenze' },
      { to: '/insights?view=skills', label: 'Skill' },
      { to: '/insights?view=certifications', label: 'Certificazioni' },
    ],
    canAccess: ({ role, levelsEnabled }) => role === 'admin' || levelsEnabled,
  },
  {
    id: 'externalCv',
    label: 'Candidati',
    description:
      'Gestisci i candidati esterni: carica il CV, ottieni le valutazioni pre-compilate dall\'AI e consulta i dettagli.',
    icon: 'user-plus',
    basePath: '/external-cv',
    home: '/external-cv',
    items: [{ to: '/external-cv', label: 'Profili', end: true }],
    // Permesso specifico della sezione CV: admin oppure `cvEnabled` (Req. 6.3, 6.4).
    canAccess: ({ role, cvEnabled }) => role === 'admin' || cvEnabled,
    // Funzionalità ancora in evoluzione (estrazione AI del CV): badge "Beta".
    beta: true,
  },
  {
    id: 'explore',
    label: 'Explore',
    description:
      'Crea esplorazioni per cercare persone e candidati con il miglior matching su un set di skill e livelli richiesti.',
    icon: 'search',
    basePath: '/explore',
    home: '/explore',
    items: [{ to: '/explore', label: 'Esplorazioni', end: true }],
    // Accesso legato alla capability "CV": admin oppure `cvEnabled` (stessa
    // guardia della sezione Candidati).
    canAccess: ({ role, cvEnabled }) => role === 'admin' || cvEnabled,
    // Funzionalità nuova, ancora in evoluzione: badge "Beta".
    beta: true,
  },
];

/**
 * Determina la sezione attiva dal path corrente, confrontando il primo segmento
 * con i `basePath`. Ritorna la sezione Assessment come default (la radice `/`
 * reindirizza lì e le rotte trasversali come `/profile` non appartengono a
 * nessuna sezione, ma Assessment è la sezione "di casa").
 */
export function sectionForPath(pathname: string): SectionDefinition {
  const match = SECTIONS.find(
    (section) =>
      pathname === section.basePath ||
      pathname.startsWith(`${section.basePath}/`),
  );
  return match ?? SECTIONS[0];
}

/** Sezioni visibili all'utente corrente, filtrate dai permessi (difensivo). */
export function accessibleSections(
  access: SectionAccess,
): readonly SectionDefinition[] {
  return SECTIONS.filter((section) => section.canAccess(access));
}
